import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminTenantSelector, { type AdminTenantOption } from '../components/AdminTenantSelector';
import { PortalDataState } from '../components/PortalDataState';
import { PortalPageHeader } from '../components/PortalPageHeader';
import { Notice } from '../components/status';
import { usePortalContext } from '../context/PortalContext';
import CollaborationInboxCard from './CollaborationInboxCard';
import CollaborationOverview from './CollaborationOverview';
import CollaborationOwnerThreadPane from './CollaborationOwnerThreadPane';
import CollaborationPartnersCard from './CollaborationPartnersCard';
import CollaborationPolicyCard from './CollaborationPolicyCard';
import CollaborationRecipientThreadPane from './CollaborationRecipientThreadPane';
import {
  acknowledgeCollaborationRecipientSourceUpdate,
  commitSupplierImport,
  createCollaborationPartner,
  decideCollaborationReturnedActionProposal,
  downloadCollaborationArtifact,
  downloadRecipientCollaborationArtifact,
  loadCollaborationData,
  loadCollaborationShareAllowance,
  saveCollaborationShareOverage,
  type CollaborationShareAllowance,
  loadCollaborationPartnerProfile,
  loadCollaborationRecipientThread,
  loadCollaborationShareThread,
  loadCollaborationStartTargets,
  loadCollaborationTenants,
  type CollaborationStartTarget,
  postCollaborationRecipientThreadMessage,
  postCollaborationShareThreadMessage,
  translateCollaborationRecipientThreadMessage,
  translateCollaborationShareThreadMessage,
  previewSupplierImport,
  purgeCollaborationArtifactBinary,
  purgeCollaborationInstanceArtifacts,
  purgeCollaborationTenantArtifacts,
  purgeCollaborationSyncPackageArtifacts,
  purgeCollaborationThreadArtifacts,
  refreshAllCollaborationWorkItemOriginVersions,
  refreshCollaborationWorkItemOriginVersion,
  releaseCollaborationArtifact,
  reviewCollaborationSyncPackage,
  revokeCollaborationParentBinding,
  revokeCollaborationShare,
  saveCollaborationPolicy,
  submitCollaborationRecipientSyncPackage,
  updateCollaborationInstanceSettings,
  updateCollaborationWorkItemState,
  withdrawCollaborationRecipientSyncPackage,
  withdrawCollaborationReturnedActionProposal,
  withdrawCollaborationSyncPackage,
  updateInboundCustomerRelationshipDisplayName,
  updateInboundCustomerRelationshipStatus,
  acknowledgeInboxItemBaseline,
  pullInboxItemLatestBaseline,
  updateInboxItemTriageStatus,
  uploadCollaborationRecipientFile
} from './collaborationRouteApi';
import CollaborationRuntimeCard from './CollaborationRuntimeCard';
import CollaborationSentSharesCard from './CollaborationSentSharesCard';
import CollaborationStartButton from './CollaborationStartButton';
import {
  policyLabel,
  type CollaborationInstance,
  type CollaborationInboxItem,
  type CollaborationPartner,
  type CollaborationPartnerProfile,
  type CollaborationPartnerFormState,
  type CollaborationPolicy,
  type CollaborationPolicyFormState,
  type CollaborationPrincipalContactChangeRequest,
  type CollaborationShareInvitation,
  type SupplierImportBatchSnapshot,
  type RecipientPolicy
} from './collaborationControlPlaneShared';
import {
  buildCollaborationOverviewModel,
  resolveCollaborationExperienceTab,
  type CollaborationExperienceTab
} from './collaborationOverviewLogic';
import {
  actionFieldUpdateLabel,
  buildRecipientFieldUpdateProposalFromForm,
  canWithdrawActiveSyncPackage,
  type CollaborationActionEditableFieldKey,
  type CollaborationFindingEditableFieldKey,
  type CollaborationSourceUpdateFieldResolutionMode,
  defaultRecipientSyncPackageFormState,
  describeThreadEvent,
  describeWorkItemState,
  extractActionProposalDetails,
  extractActionFieldUpdates,
  extractFindingFieldUpdates,
  extractSourceUpdateMode,
  findingFieldUpdateLabel,
  formatBytes,
  getActionSourceUpdateReviewFields,
  getFindingSourceUpdateReviewFields,
  getLatestDraftFieldUpdatePackage,
  getLatestPendingSourceUpdatePackage,
  getWorkItemSubmissionBlockReason,
  parseJsonSafe,
  populateRecipientSyncPackageFormFromFieldUpdateDraft,
  relativeTime,
  recipientSyncPackageFormHasFieldUpdateContent,
  type CollaborationThreadSnapshot,
  type CollaborationThreadSyncPackage,
  type CollaborationThreadWorkItem,
  type RecipientSyncPackageFormState
} from './collaborationThreadShared';
import {
  collaborationSeenStorageKey,
  isCollaborationThreadNew,
  markCollaborationThreadSeen,
  parseCollaborationSeenState,
  receivedWorkActivityAt,
  sentWorkActivityAt,
  summariseCollaborationEvidence,
  type CollaborationEvidenceSummary,
  type CollaborationSeenState,
  type CollaborationWorkDirection
} from './collaborationWorkQueueLogic';

type CollaborationEditableFieldKey = CollaborationFindingEditableFieldKey | CollaborationActionEditableFieldKey;
type RecipientSourceUpdateFieldResolutionState = Record<
  string,
  Partial<Record<CollaborationEditableFieldKey, CollaborationSourceUpdateFieldResolutionMode>>
>;

const buildRecipientFieldUpdateFormForWorkItem = (
  snapshot: CollaborationThreadSnapshot,
  workItemId: string,
  actorEmail?: string | null
): RecipientSyncPackageFormState => {
  const draftPackage = getLatestDraftFieldUpdatePackage(snapshot.syncPackages, workItemId, actorEmail);
  return populateRecipientSyncPackageFormFromFieldUpdateDraft(workItemId, draftPackage);
};

const buildRebasedFindingDraftFromForm = (
  snapshot: CollaborationThreadSnapshot,
  workItemId: string,
  form: RecipientSyncPackageFormState,
  actorEmail?: string | null
): {
  summary?: string;
  fieldUpdates?: Array<{
    fieldKey: 'description' | 'reference' | 'rootCause' | 'evidence';
    proposedValue?: string | null;
  }>;
} | undefined => {
  if (form.packageType === 'field_update' && form.workItemId === workItemId) {
    const proposal = buildRecipientFieldUpdateProposalFromForm(
      form,
      'finding',
      snapshot.fieldUpdateContext?.findingFieldSnapshot
    );
    if (!proposal.summary && proposal.fieldUpdates.length === 0) {
      return undefined;
    }
    return {
      summary: proposal.summary || undefined,
      fieldUpdates: proposal.fieldUpdates.map((entry) => ({
        fieldKey: entry.fieldKey,
        proposedValue: entry.proposedValue
      }))
    };
  }
  const savedDraft = getLatestDraftFieldUpdatePackage(snapshot.syncPackages, workItemId, actorEmail);
  if (!savedDraft?.proposalJson) {
    return undefined;
  }
  const payload = parseJsonSafe(savedDraft.proposalJson);
  const fieldUpdates = extractFindingFieldUpdates(payload);
  const summary = String(payload.summary || '').trim();
  if (!summary && fieldUpdates.length === 0) {
    return undefined;
  }
  return {
    summary: summary || undefined,
    fieldUpdates: fieldUpdates.map((entry) => ({
      fieldKey: entry.fieldKey,
      proposedValue: entry.proposedValue
    }))
  };
};

const buildRebasedActionDraftFromForm = (
  snapshot: CollaborationThreadSnapshot,
  workItemId: string,
  form: RecipientSyncPackageFormState,
  actorEmail?: string | null
): {
  summary?: string;
  fieldUpdates?: Array<{
    fieldKey: 'title' | 'status' | 'ownerEmail' | 'targetDate';
    proposedValue?: string | null;
  }>;
} | undefined => {
  const actionWorkItem = snapshot.workItems.find((entry) => entry.workItemId === workItemId && entry.objectType === 'action');
  const actionFieldSnapshot = actionWorkItem
    ? snapshot.fieldUpdateContext?.actionFieldContextsByObjectId[actionWorkItem.objectId]?.actionFieldSnapshot
    : undefined;
  if (form.packageType === 'field_update' && form.workItemId === workItemId) {
    const proposal = buildRecipientFieldUpdateProposalFromForm(form, 'action', actionFieldSnapshot);
    if (!proposal.summary && proposal.fieldUpdates.length === 0) {
      return undefined;
    }
    return {
      summary: proposal.summary || undefined,
      fieldUpdates: proposal.fieldUpdates.map((entry) => ({
        fieldKey: entry.fieldKey as 'title' | 'status' | 'ownerEmail' | 'targetDate',
        proposedValue: entry.proposedValue
      }))
    };
  }
  const savedDraft = getLatestDraftFieldUpdatePackage(snapshot.syncPackages, workItemId, actorEmail);
  if (!savedDraft?.proposalJson) {
    return undefined;
  }
  const payload = parseJsonSafe(savedDraft.proposalJson);
  const fieldUpdates = extractActionFieldUpdates(payload);
  const summary = String(payload.summary || '').trim();
  if (!summary && fieldUpdates.length === 0) {
    return undefined;
  }
  return {
    summary: summary || undefined,
    fieldUpdates: fieldUpdates.map((entry) => ({
      fieldKey: entry.fieldKey,
      proposedValue: entry.proposedValue
    }))
  };
};

const COLLABORATION_TABS: ReadonlyArray<{ key: CollaborationExperienceTab; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'partners', label: 'Suppliers' },
  { key: 'outbound', label: 'Sent work' },
  { key: 'inbound', label: 'Received work' },
  { key: 'settings', label: 'Configuration' }
];

const Collaboration = (): JSX.Element => {
  const { tenant, entitlements } = usePortalContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tenantParam = searchParams.get('tenantId') || '';
  const shareInvitationIdParam = searchParams.get('shareInvitationId') || '';
  const inboxItemIdParam = searchParams.get('inboxItemId') || '';
  const canManage = tenant?.capabilities?.canManageSupplierCollaboration === true;
  // Tracker #121: supplier set-up and configuration are for collaboration
  // administrators; everyone else sees Overview, Sent work and Received work.
  const requestedTab = resolveCollaborationExperienceTab(tabParam);
  const activeTab: CollaborationExperienceTab =
    !canManage && (requestedTab === 'partners' || requestedTab === 'settings') ? 'overview' : requestedTab;
  const visibleTabs = COLLABORATION_TABS.filter(
    (tab) => canManage || (tab.key !== 'partners' && tab.key !== 'settings')
  );
  const setActiveTab = (tab: CollaborationExperienceTab): void => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'overview') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    if (tab !== 'outbound') next.delete('shareInvitationId');
    if (tab !== 'inbound') next.delete('inboxItemId');
    setSearchParams(next, { replace: true });
  };
  // Tracker #117: Free (text-only) may also start and manage outbound work.
  const isTextOnlyShare = !entitlements.includes('Audit.ExternalShareMetered') &&
    entitlements.includes('Audit.ExternalShareTextOnly');
  const canInitiateShare = entitlements.includes('Audit.ExternalShareMetered') || isTextOnlyShare;
  const [shareAllowance, setShareAllowance] = useState<CollaborationShareAllowance | null>(null);
  const [shareOverageSaving, setShareOverageSaving] = useState(false);

  const [deployableTenants, setDeployableTenants] = useState<AdminTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [startTargets, setStartTargets] = useState<CollaborationStartTarget[]>([]);

  const [policy, setPolicy] = useState<CollaborationPolicy | null>(null);
  const [partners, setPartners] = useState<CollaborationPartner[]>([]);
  const [instances, setInstances] = useState<CollaborationInstance[]>([]);
  const [inbox, setInbox] = useState<CollaborationInboxItem[]>([]);
  const [shares, setShares] = useState<CollaborationShareInvitation[]>([]);
  const [principalChangeRequests, setPrincipalChangeRequests] = useState<CollaborationPrincipalContactChangeRequest[]>([]);
  const [overviewProfiles, setOverviewProfiles] = useState<CollaborationPartnerProfile[]>([]);
  const [overviewInvitationEvidenceIncomplete, setOverviewInvitationEvidenceIncomplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [seenLedger, setSeenLedger] = useState<{
    storageKey: string;
    values: CollaborationSeenState;
  }>({ storageKey: '', values: {} });
  const [sentEvidenceByShareId, setSentEvidenceByShareId] = useState<Map<string, CollaborationEvidenceSummary>>(
    new Map()
  );
  const [receivedEvidenceByInboxItemId, setReceivedEvidenceByInboxItemId] = useState<Map<string, CollaborationEvidenceSummary>>(
    new Map()
  );

  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policyForm, setPolicyForm] = useState<CollaborationPolicyFormState>({
    recipientPolicy: 'registered_contacts_only',
    allowUnregisteredView: true,
    requireVerifiedEmail: true
  });

  const [savingPartnerId, setSavingPartnerId] = useState('');
  const [supplierImportFile, setSupplierImportFile] = useState<File | null>(null);
  const [supplierImportBatch, setSupplierImportBatch] = useState<SupplierImportBatchSnapshot | null>(null);
  const [previewingSupplierImport, setPreviewingSupplierImport] = useState(false);
  const [committingSupplierImport, setCommittingSupplierImport] = useState(false);
  const [partnerForm, setPartnerForm] = useState<CollaborationPartnerFormState>({
    displayName: '',
    primaryDomain: '',
    externalSupplierReference: '',
    defaultReplyToEmail: '',
    footprintMode: 'portal_only',
    supplierCategory: '',
    riskTier: ''
  });
  const [savingInstanceId, setSavingInstanceId] = useState('');
  const [revokingParentBindingId, setRevokingParentBindingId] = useState('');
  const [purgingTenantArtifacts, setPurgingTenantArtifacts] = useState(false);
  const [purgingInstanceArtifactsId, setPurgingInstanceArtifactsId] = useState('');

  const [revokingShareId, setRevokingShareId] = useState('');
  const [selectedThreadShareId, setSelectedThreadShareId] = useState('');
  const [threadSnapshot, setThreadSnapshot] = useState<CollaborationThreadSnapshot | null>(null);
  const [threadLoadingShareId, setThreadLoadingShareId] = useState('');
  const [threadMessageBody, setThreadMessageBody] = useState('');
  const [postingThreadMessage, setPostingThreadMessage] = useState(false);
  const [translatingMessageId, setTranslatingMessageId] = useState('');
  const [reviewingSyncPackageId, setReviewingSyncPackageId] = useState('');
  const [withdrawingSyncPackageId, setWithdrawingSyncPackageId] = useState('');
  const [syncPackageReviewNotes, setSyncPackageReviewNotes] = useState<Record<string, string>>({});
  const [refreshingWorkItemId, setRefreshingWorkItemId] = useState('');
  const [refreshingAllWorkItems, setRefreshingAllWorkItems] = useState(false);
  const [updatingWorkItemStateId, setUpdatingWorkItemStateId] = useState('');
  const [releasingArtifactId, setReleasingArtifactId] = useState('');
  const [downloadingArtifactId, setDownloadingArtifactId] = useState('');
  const [purgingArtifactId, setPurgingArtifactId] = useState('');
  const [purgingSyncPackageArtifactsId, setPurgingSyncPackageArtifactsId] = useState('');
  const [purgingAllThreadArtifacts, setPurgingAllThreadArtifacts] = useState(false);
  const [decidingReturnedActionProposalId, setDecidingReturnedActionProposalId] = useState('');
  const [selectedRecipientInboxItemId, setSelectedRecipientInboxItemId] = useState('');
  const [updatingInboundCustomerRelationshipId, setUpdatingInboundCustomerRelationshipId] = useState('');
  const [editingInboundCustomerRelationshipId, setEditingInboundCustomerRelationshipId] = useState('');
  const [inboundCustomerDisplayNameDraft, setInboundCustomerDisplayNameDraft] = useState('');
  const [inboundCustomerDisplayNameRowVersionDraft, setInboundCustomerDisplayNameRowVersionDraft] = useState('');
  const [savingInboundCustomerDisplayNameId, setSavingInboundCustomerDisplayNameId] = useState('');
  const [triagingInboxItemId, setTriagingInboxItemId] = useState('');
  const [baselineActionInboxItemId, setBaselineActionInboxItemId] = useState('');
  const [recipientThreadSnapshot, setRecipientThreadSnapshot] = useState<CollaborationThreadSnapshot | null>(null);
  const [recipientThreadLoadingInboxItemId, setRecipientThreadLoadingInboxItemId] = useState('');
  const [recipientThreadMessageBody, setRecipientThreadMessageBody] = useState('');
  const [postingRecipientThreadMessage, setPostingRecipientThreadMessage] = useState(false);
  const [submittingRecipientSyncPackage, setSubmittingRecipientSyncPackage] = useState(false);
  const [savingRecipientSyncPackageDraft, setSavingRecipientSyncPackageDraft] = useState(false);
  const [withdrawingRecipientSyncPackageId, setWithdrawingRecipientSyncPackageId] = useState('');
  const [acknowledgingRecipientSourceUpdateId, setAcknowledgingRecipientSourceUpdateId] = useState('');
  const [recipientSourceUpdateFieldResolutions, setRecipientSourceUpdateFieldResolutions] =
    useState<RecipientSourceUpdateFieldResolutionState>({});
  const [withdrawingRecipientReturnedActionProposalId, setWithdrawingRecipientReturnedActionProposalId] = useState('');
  const [downloadingRecipientArtifactId, setDownloadingRecipientArtifactId] = useState('');
  const [recipientSyncPackageForm, setRecipientSyncPackageForm] = useState<RecipientSyncPackageFormState>(
    defaultRecipientSyncPackageFormState()
  );
  const openedDeepLinkSharesRef = useRef<Set<string>>(new Set());
  const openedDeepLinkInboxItemsRef = useRef<Set<string>>(new Set());
  const instanceById = useMemo(
    () => new Map(instances.map((instance) => [instance.instanceId, instance])),
    [instances]
  );
  const selectedRecipientInboxItem = useMemo(
    () => inbox.find((item) => item.inboxItemId === selectedRecipientInboxItemId) ?? null,
    [inbox, selectedRecipientInboxItemId]
  );
  const partnerNameById = useMemo(
    () =>
      new Map(
        partners.map((partner) => [partner.partnerId, partner.displayName] as const)
      ),
    [partners]
  );
  const seenStorageKey = useMemo(
    () => selectedTenantId && tenant?.userEmail
      ? collaborationSeenStorageKey({ tenantId: selectedTenantId, userEmail: tenant.userEmail })
      : '',
    [selectedTenantId, tenant?.userEmail]
  );
  const seenValues = seenLedger.storageKey === seenStorageKey ? seenLedger.values : {};
  const newSentShareIds = useMemo(
    () => new Set(
      shares
        .filter((share) => isCollaborationThreadNew({
          state: seenValues,
          direction: 'sent',
          threadId: share.shareInvitationId,
          activityAt: sentWorkActivityAt(share),
          createdAt: share.createdAt
        }))
        .map((share) => share.shareInvitationId)
    ),
    [seenValues, shares]
  );
  const newInboxItemIds = useMemo(
    () => new Set(
      inbox
        .filter((item) => isCollaborationThreadNew({
          state: seenValues,
          direction: 'received',
          threadId: item.inboxItemId,
          activityAt: receivedWorkActivityAt(item)
        }))
        .map((item) => item.inboxItemId)
    ),
    [inbox, seenValues]
  );
  const overviewModel = useMemo(
    () =>
      buildCollaborationOverviewModel({
        partners,
        profiles: overviewProfiles,
        shares,
        inbox,
        principalChangeRequests,
        newSentActivityCount: newSentShareIds.size,
        newReceivedActivityCount: newInboxItemIds.size
      }),
    [inbox, newInboxItemIds, newSentShareIds, overviewProfiles, partners, principalChangeRequests, shares]
  );
  const selectedRecipientWorkItem = recipientThreadSnapshot?.workItems.find(
    (entry) => entry.workItemId === recipientSyncPackageForm.workItemId
  );
  const selectedRecipientDraftSyncPackage = useMemo<CollaborationThreadSyncPackage | null>(
    () =>
      recipientThreadSnapshot && recipientSyncPackageForm.workItemId
        ? getLatestDraftFieldUpdatePackage(
            recipientThreadSnapshot.syncPackages,
            recipientSyncPackageForm.workItemId,
            tenant?.userEmail
          )
        : null,
    [recipientSyncPackageForm.workItemId, recipientThreadSnapshot, tenant?.userEmail]
  );
  const selectedRecipientPendingSourceUpdate = useMemo<CollaborationThreadSyncPackage | null>(
    () =>
      recipientThreadSnapshot && recipientSyncPackageForm.workItemId
        ? getLatestPendingSourceUpdatePackage(
            recipientThreadSnapshot.syncPackages,
            recipientSyncPackageForm.workItemId
          )
        : null,
    [recipientSyncPackageForm.workItemId, recipientThreadSnapshot]
  );
  const selectedRecipientSourceUpdateReviewFields = useMemo(
    () => {
      const selectedWorkItem = recipientThreadSnapshot?.workItems.find(
        (entry) => entry.workItemId === recipientSyncPackageForm.workItemId
      );
      const baseFields =
        selectedWorkItem?.objectType === 'action'
          ? getActionSourceUpdateReviewFields(selectedRecipientPendingSourceUpdate, recipientSyncPackageForm).map((entry) => ({
              ...entry,
              fieldLabel: actionFieldUpdateLabel(entry.fieldKey)
            }))
          : getFindingSourceUpdateReviewFields(selectedRecipientPendingSourceUpdate, recipientSyncPackageForm).map((entry) => ({
              ...entry,
              fieldLabel: findingFieldUpdateLabel(entry.fieldKey)
            }));
      return baseFields.map((entry) => ({
        ...entry,
        resolutionMode:
          selectedRecipientPendingSourceUpdate
            ? recipientSourceUpdateFieldResolutions[selectedRecipientPendingSourceUpdate.syncPackageId]?.[entry.fieldKey]
            : undefined
      }));
    },
    [recipientSourceUpdateFieldResolutions, recipientSyncPackageForm, recipientThreadSnapshot?.workItems, selectedRecipientPendingSourceUpdate]
  );
  const selectedRecipientPendingSourceUpdateRequiresResolution =
    Boolean(selectedRecipientPendingSourceUpdate) &&
    selectedRecipientPendingSourceUpdate?.proposalJson
      ? extractSourceUpdateMode(parseJsonSafe(selectedRecipientPendingSourceUpdate.proposalJson)) === 'published_new_baseline'
      : false;
  const selectedRecipientUnresolvedSourceUpdateFieldLabels = useMemo(
    () =>
      selectedRecipientPendingSourceUpdateRequiresResolution
        ? selectedRecipientSourceUpdateReviewFields
            .filter((entry) => !entry.resolutionMode)
            .map((entry) => entry.fieldLabel)
        : [],
    [selectedRecipientPendingSourceUpdateRequiresResolution, selectedRecipientSourceUpdateReviewFields]
  );
  const loadTenants = async (): Promise<void> => {
    try {
      const payload = await loadCollaborationTenants();
      setDeployableTenants(payload.tenants);
      setSelectedTenantId((current) => {
        if (current) {
          return current;
        }
        return payload.tenants.some((entry) => entry.tenantId === tenantParam)
          ? tenantParam
          : payload.defaultTenantId;
      });
    } catch (loadError) {
      setError((loadError as Error).message);
    }
  };

  const loadAll = async (): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const data = await loadCollaborationData({
        tenantId: selectedTenantId,
        canManage,
        canInitiateShare
      });
      const resolvedPolicy = data.policy;
      setPolicy(resolvedPolicy);
      if (resolvedPolicy) {
        setPolicyForm({
          recipientPolicy: resolvedPolicy.recipientPolicy,
          allowUnregisteredView: resolvedPolicy.allowUnregisteredView,
          requireVerifiedEmail: resolvedPolicy.requireVerifiedEmail
        });
      }
      setPartners(data.partners);
      setInstances(data.instances);
      setPrincipalChangeRequests(data.principalChangeRequests);
      setInbox(data.inbox);
      setShares(data.shares);
    } catch (loadError) {
      setError((loadError as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTenants();
  }, []);

  useEffect(() => {
    void loadAll();
  }, [canInitiateShare, canManage, selectedTenantId]);

  // Tracker #121: the installations "Start collaboration" can open.
  useEffect(() => {
    if (!selectedTenantId) {
      setStartTargets([]);
      return;
    }
    let cancelled = false;
    loadCollaborationStartTargets(selectedTenantId)
      .then((targets) => {
        if (!cancelled) setStartTargets(targets);
      })
      .catch(() => {
        if (!cancelled) setStartTargets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantId]);

  const loadShareAllowance = async (): Promise<void> => {
    if (!selectedTenantId || !isTextOnlyShare) {
      setShareAllowance(null);
      return;
    }
    try {
      setShareAllowance(await loadCollaborationShareAllowance(selectedTenantId));
    } catch {
      // Informational only; the share route enforces the allowance itself.
      setShareAllowance(null);
    }
  };

  useEffect(() => {
    void loadShareAllowance();
  }, [isTextOnlyShare, selectedTenantId]);

  const toggleShareOverage = async (allowShareOverage: boolean): Promise<void> => {
    setShareOverageSaving(true);
    setError(null);
    try {
      await saveCollaborationShareOverage({ tenantId: selectedTenantId, allowShareOverage });
      await loadShareAllowance();
    } catch (saveError) {
      setError((saveError as Error).message);
    } finally {
      setShareOverageSaving(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'outbound') {
      setSelectedThreadShareId('');
      setThreadSnapshot(null);
    }
    if (activeTab !== 'inbound') {
      setSelectedRecipientInboxItemId('');
      setRecipientThreadSnapshot(null);
    }
  }, [activeTab]);

  useEffect(() => {
    setSelectedThreadShareId('');
    setThreadSnapshot(null);
    setSelectedRecipientInboxItemId('');
    setRecipientThreadSnapshot(null);
    openedDeepLinkSharesRef.current.clear();
    openedDeepLinkInboxItemsRef.current.clear();
  }, [selectedTenantId]);

  useEffect(() => {
    if (!seenStorageKey) {
      setSeenLedger({ storageKey: '', values: {} });
      return;
    }
    let values: CollaborationSeenState = {};
    try {
      values = parseCollaborationSeenState(window.localStorage.getItem(seenStorageKey));
    } catch {
      // Browser privacy/storage policies must not prevent collaboration work.
    }
    setSeenLedger({ storageKey: seenStorageKey, values });
  }, [seenStorageKey]);

  useEffect(() => {
    if (loading || !selectedTenantId) {
      return;
    }
    let cancelled = false;
    if (activeTab === 'outbound') {
      setSentEvidenceByShareId(new Map());
      void Promise.allSettled(
        shares.map(async (share) => ({
          id: share.shareInvitationId,
          summary: summariseCollaborationEvidence((await loadCollaborationShareThread({
            tenantId: selectedTenantId,
            shareInvitationId: share.shareInvitationId
          })).artifacts)
        }))
      ).then((results) => {
        if (cancelled) return;
        setSentEvidenceByShareId(new Map(
          results.flatMap((result) => result.status === 'fulfilled' ? [[result.value.id, result.value.summary] as const] : [])
        ));
      });
    }

    if (activeTab === 'inbound') {
      setReceivedEvidenceByInboxItemId(new Map());
      const openableInboxItems = inbox.filter((item) => item.runtimeAccess.canOpenInstallationThread);
      void Promise.allSettled(
        openableInboxItems.map(async (item) => ({
          id: item.inboxItemId,
          summary: summariseCollaborationEvidence((await loadCollaborationRecipientThread({
            tenantId: selectedTenantId,
            inboxItemId: item.inboxItemId
          })).artifacts)
        }))
      ).then((results) => {
        if (cancelled) return;
        setReceivedEvidenceByInboxItemId(new Map(
          results.flatMap((result) => result.status === 'fulfilled' ? [[result.value.id, result.value.summary] as const] : [])
        ));
      });
    }

    return () => {
      cancelled = true;
    };
  }, [activeTab, inbox, loading, selectedTenantId, shares]);

  useEffect(() => {
    if (!canManage || !selectedTenantId) {
      setOverviewProfiles([]);
      setOverviewInvitationEvidenceIncomplete(false);
      return;
    }
    const setupPartners = partners.filter((partner) =>
      ['draft', 'awaiting_supplier_registration', 'awaiting_activation', 'declined'].includes(partner.status)
    );
    if (setupPartners.length === 0) {
      setOverviewProfiles([]);
      setOverviewInvitationEvidenceIncomplete(false);
      return;
    }

    let cancelled = false;
    void Promise.allSettled(
      setupPartners.map((partner) =>
        loadCollaborationPartnerProfile({ tenantId: selectedTenantId, partnerId: partner.partnerId })
      )
    ).then((results) => {
      if (cancelled) return;
      setOverviewProfiles(
        results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []))
      );
      setOverviewInvitationEvidenceIncomplete(results.some((result) => result.status === 'rejected'));
    });

    return () => {
      cancelled = true;
    };
  }, [canManage, partners, selectedTenantId]);

  const savePolicy = async (): Promise<void> => {
    setSavingPolicy(true);
    setNotice(null);
    setError(null);
    try {
      const nextPolicy = await saveCollaborationPolicy({
        tenantId: selectedTenantId,
        policyForm
      });
      if (nextPolicy) {
        setPolicy(nextPolicy);
      }
      setNotice('Supplier quality policy updated.');
    } catch (saveError) {
      setError((saveError as Error).message);
    } finally {
      setSavingPolicy(false);
    }
  };

  const createPartner = async (): Promise<void> => {
    setSavingPartnerId('__new__');
    setNotice(null);
    setError(null);
    try {
      const createdPartner = await createCollaborationPartner({
        tenantId: selectedTenantId,
        partnerForm
      });
      setPartnerForm({
        displayName: '',
        primaryDomain: '',
        externalSupplierReference: '',
        defaultReplyToEmail: '',
        footprintMode: 'portal_only',
        supplierCategory: '',
        riskTier: ''
      });
      navigate(
        `/collaboration/partners/${encodeURIComponent(createdPartner.partnerId)}?tenantId=${encodeURIComponent(selectedTenantId)}`
      );
    } catch (partnerError) {
      setError((partnerError as Error).message);
    } finally {
      setSavingPartnerId('');
    }
  };

  const previewSupplierCsvImport = async (): Promise<void> => {
    if (!selectedTenantId || !supplierImportFile) {
      return;
    }
    setPreviewingSupplierImport(true);
    setNotice(null);
    setError(null);
    try {
      const batch = await previewSupplierImport({
        tenantId: selectedTenantId,
        file: supplierImportFile
      });
      setSupplierImportBatch(batch);
      setNotice('Supplier import preview created.');
    } catch (importError) {
      setError((importError as Error).message);
    } finally {
      setPreviewingSupplierImport(false);
    }
  };

  const commitSupplierCsvImport = async (): Promise<void> => {
    if (!selectedTenantId || !supplierImportBatch) {
      return;
    }
    const rowIds = supplierImportBatch.rows
      .filter((row) => row.validationStatus === 'valid')
      .map((row) => row.importRowId);
    if (rowIds.length === 0) {
      setError('No valid supplier import rows are available to commit.');
      return;
    }
    setCommittingSupplierImport(true);
    setNotice(null);
    setError(null);
    try {
      const batch = await commitSupplierImport({
        tenantId: selectedTenantId,
        importBatchId: supplierImportBatch.importBatchId,
        rowIds
      });
      setSupplierImportBatch(batch);
      setNotice('Supplier import committed as draft suppliers.');
      await loadAll();
    } catch (importError) {
      setError((importError as Error).message);
    } finally {
      setCommittingSupplierImport(false);
    }
  };

  const updateInboundCustomerRelationship = async (
    relationshipId: string,
    status: 'accepted' | 'blocked'
  ): Promise<void> => {
    if (!relationshipId) {
      return;
    }
    setUpdatingInboundCustomerRelationshipId(relationshipId);
    setNotice(null);
    setError(null);
    try {
      await updateInboundCustomerRelationshipStatus({
        tenantId: selectedTenantId,
        inboundCustomerRelationshipId: relationshipId,
        status
      });
      setNotice(status === 'blocked' ? 'Inbound customer blocked.' : 'Inbound customer relationship accepted.');
      await loadAll();
    } catch (updateError) {
      setError((updateError as Error).message);
    } finally {
      setUpdatingInboundCustomerRelationshipId('');
    }
  };

  const startEditingInboundCustomerDisplayName = (
    relationshipId: string,
    currentDisplayName: string,
    rowVersion: string
  ): void => {
    setEditingInboundCustomerRelationshipId(relationshipId);
    setInboundCustomerDisplayNameDraft(currentDisplayName);
    setInboundCustomerDisplayNameRowVersionDraft(rowVersion);
    setNotice(null);
    setError(null);
  };

  const cancelEditingInboundCustomerDisplayName = (): void => {
    setEditingInboundCustomerRelationshipId('');
    setInboundCustomerDisplayNameDraft('');
    setInboundCustomerDisplayNameRowVersionDraft('');
  };

  const saveInboundCustomerDisplayName = async (relationshipId: string): Promise<void> => {
    const sourceCustomerDisplayName = inboundCustomerDisplayNameDraft.trim().replace(/\s+/g, ' ');
    if (!relationshipId || !sourceCustomerDisplayName) {
      setError('Customer display name is required.');
      return;
    }
    setSavingInboundCustomerDisplayNameId(relationshipId);
    setNotice(null);
    setError(null);
    try {
      await updateInboundCustomerRelationshipDisplayName({
        tenantId: selectedTenantId,
        inboundCustomerRelationshipId: relationshipId,
        sourceCustomerDisplayName,
        expectedRowVersion: inboundCustomerDisplayNameRowVersionDraft
      });
      setNotice('Inbound customer display name updated.');
      setEditingInboundCustomerRelationshipId('');
      setInboundCustomerDisplayNameDraft('');
      setInboundCustomerDisplayNameRowVersionDraft('');
      await loadAll();
    } catch (updateError) {
      setError((updateError as Error).message);
      if ((updateError as Error).message.includes('Another admin updated this customer display name')) {
        setEditingInboundCustomerRelationshipId('');
        setInboundCustomerDisplayNameDraft('');
        setInboundCustomerDisplayNameRowVersionDraft('');
        await loadAll();
      }
    } finally {
      setSavingInboundCustomerDisplayNameId('');
    }
  };

  const updateInboxItemTriage = async (
    inboxItemId: string,
    status: 'accepted' | 'rejected' | 'archived'
  ): Promise<void> => {
    if (!inboxItemId) {
      return;
    }
    setTriagingInboxItemId(inboxItemId);
    setNotice(null);
    setError(null);
    try {
      await updateInboxItemTriageStatus({
        tenantId: selectedTenantId,
        inboxItemId,
        status
      });
      setNotice(`Inbound item marked ${status}.`);
      await loadAll();
    } catch (updateError) {
      setError((updateError as Error).message);
    } finally {
      setTriagingInboxItemId('');
    }
  };

  const pullInboxBaseline = async (inboxItemId: string): Promise<void> => {
    if (!inboxItemId) {
      return;
    }
    setBaselineActionInboxItemId(inboxItemId);
    setNotice(null);
    setError(null);
    try {
      await pullInboxItemLatestBaseline({
        tenantId: selectedTenantId,
        inboxItemId
      });
      setNotice('Latest source baseline pulled.');
      await loadAll();
      if (selectedRecipientInboxItemId === inboxItemId && recipientThreadSnapshot) {
        await loadRecipientThread(inboxItemId);
      }
    } catch (updateError) {
      setError((updateError as Error).message);
    } finally {
      setBaselineActionInboxItemId('');
    }
  };

  const acknowledgeInboxBaseline = async (inboxItemId: string): Promise<void> => {
    if (!inboxItemId) {
      return;
    }
    setBaselineActionInboxItemId(inboxItemId);
    setNotice(null);
    setError(null);
    try {
      await acknowledgeInboxItemBaseline({
        tenantId: selectedTenantId,
        inboxItemId
      });
      setNotice('Pulled source baseline acknowledged.');
      await loadAll();
    } catch (updateError) {
      setError((updateError as Error).message);
    } finally {
      setBaselineActionInboxItemId('');
    }
  };

  const revokeShare = async (shareInvitationId: string): Promise<void> => {
    setRevokingShareId(shareInvitationId);
    setNotice(null);
    setError(null);
    try {
      await revokeCollaborationShare({
        tenantId: selectedTenantId,
        shareInvitationId
      });
      setNotice('Supplier quality share revoked.');
      await loadAll();
    } catch (revokeError) {
      setError((revokeError as Error).message);
    } finally {
      setRevokingShareId('');
    }
  };

  const saveInstanceSettings = async (
    instance: CollaborationInstance,
    nextValues?: Partial<Pick<CollaborationInstance, 'acceptsCollaboration' | 'collaborationOutbound' | 'collaborationInbound' | 'defaultCollaborationReplyToEmail'>>
  ): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setSavingInstanceId(instance.instanceId);
    setNotice(null);
    setError(null);
    try {
      const updatedInstance = await updateCollaborationInstanceSettings({
        tenantId: selectedTenantId,
        instance,
        acceptsCollaboration: nextValues?.acceptsCollaboration ?? instance.acceptsCollaboration,
        collaborationOutbound: nextValues?.collaborationOutbound ?? instance.collaborationOutbound,
        collaborationInbound: nextValues?.collaborationInbound ?? instance.collaborationInbound,
        defaultCollaborationReplyToEmail:
          nextValues?.defaultCollaborationReplyToEmail ?? instance.defaultCollaborationReplyToEmail
      });
      setInstances((current) =>
        current.map((entry) => (entry.instanceId === updatedInstance.instanceId ? updatedInstance : entry))
      );
      setNotice('Supplier quality settings updated.');
    } catch (instanceError) {
      setError((instanceError as Error).message);
    } finally {
      setSavingInstanceId('');
    }
  };

  const revokeParentBinding = async (
    instance: CollaborationInstance,
    parentBindingId: string
  ): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    if (
      !window.confirm(
        `Revoke the parent connection for ${instance.displayName || instance.siteUrl}? Received work already stored will remain, but this relationship will no longer be active.`
      )
    ) {
      return;
    }
    setRevokingParentBindingId(parentBindingId);
    setNotice(null);
    setError(null);
    try {
      const updatedInstance = await revokeCollaborationParentBinding({
        tenantId: selectedTenantId,
        instance,
        parentBindingId
      });
      setInstances((current) =>
        current.map((entry) => (entry.instanceId === updatedInstance.instanceId ? updatedInstance : entry))
      );
      setNotice('Installation parent binding revoked.');
    } catch (bindingError) {
      setError((bindingError as Error).message);
    } finally {
      setRevokingParentBindingId('');
    }
  };

  const purgeTenantArtifacts = async (): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    if (!window.confirm('Purge all stored supplier quality artifact binaries for this tenant? Metadata and history will remain.')) {
      return;
    }
    setPurgingTenantArtifacts(true);
    setNotice(null);
    setError(null);
    try {
      const payload = await purgeCollaborationTenantArtifacts({
        tenantId: selectedTenantId,
        reason: 'Tenant-admin requested full collaboration artifact purge from portal runtime controls.'
      });
      setNotice(
        payload.purgedArtifactCount > 0
          ? `Purged ${payload.purgedArtifactCount} tenant supplier quality artifact ${payload.purgedArtifactCount === 1 ? 'binary' : 'binaries'}. Metadata and history remain available.`
          : 'All tenant supplier quality artifact binaries were already purged.'
      );
      await loadAll();
      if (selectedThreadShareId) {
        await loadShareThread(selectedThreadShareId);
      }
      if (selectedRecipientInboxItemId) {
        await loadRecipientThread(selectedRecipientInboxItemId);
      }
    } catch (purgeError) {
      setError((purgeError as Error).message);
    } finally {
      setPurgingTenantArtifacts(false);
    }
  };

  const purgeInstanceArtifacts = async (instance: CollaborationInstance): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    if (
      !window.confirm(
        `Purge all stored supplier quality artifact binaries associated with ${instance.displayName || instance.siteUrl}? Metadata and history will remain.`
      )
    ) {
      return;
    }
    setPurgingInstanceArtifactsId(instance.instanceId);
    setNotice(null);
    setError(null);
    try {
      const payload = await purgeCollaborationInstanceArtifacts({
        tenantId: selectedTenantId,
        instanceId: instance.instanceId,
        reason: 'Tenant-admin requested instance-scoped collaboration artifact purge from portal runtime controls.'
      });
      setNotice(
        payload.purgedArtifactCount > 0
          ? `Purged ${payload.purgedArtifactCount} supplier quality artifact ${payload.purgedArtifactCount === 1 ? 'binary' : 'binaries'} for ${instance.displayName || instance.siteUrl}. Metadata and history remain available.`
          : `All supplier quality artifact binaries for ${instance.displayName || instance.siteUrl} were already purged.`
      );
      await loadAll();
      if (selectedThreadShareId) {
        await loadShareThread(selectedThreadShareId);
      }
      if (selectedRecipientInboxItemId) {
        await loadRecipientThread(selectedRecipientInboxItemId);
      }
    } catch (purgeError) {
      setError((purgeError as Error).message);
    } finally {
      setPurgingInstanceArtifactsId('');
    }
  };

  const recordThreadSeen = (
    direction: CollaborationWorkDirection,
    threadId: string,
    activityAt: string
  ): void => {
    if (!seenStorageKey) return;
    setSeenLedger((current) => {
      const values = current.storageKey === seenStorageKey ? current.values : {};
      const nextValues = markCollaborationThreadSeen({ state: values, direction, threadId, activityAt });
      try {
        window.localStorage.setItem(seenStorageKey, JSON.stringify(nextValues));
      } catch {
        // The current view remains accurate even when browser storage is unavailable.
      }
      return { storageKey: seenStorageKey, values: nextValues };
    });
  };

  const loadShareThread = async (shareInvitationId: string): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setThreadLoadingShareId(shareInvitationId);
    setError(null);
    try {
      const payload = await loadCollaborationShareThread({
        tenantId: selectedTenantId,
        shareInvitationId
      });
      setSelectedThreadShareId(shareInvitationId);
      setThreadSnapshot(payload);
      setThreadMessageBody('');
      setSyncPackageReviewNotes({});
      const share = shares.find((entry) => entry.shareInvitationId === shareInvitationId);
      recordThreadSeen('sent', shareInvitationId, share ? sentWorkActivityAt(share) : payload.thread.updatedAtUtc);
    } catch (threadError) {
      setError((threadError as Error).message);
    } finally {
      setThreadLoadingShareId('');
    }
  };

  const openShareThread = (shareInvitationId: string): void => {
    if (!selectedTenantId) return;
    const signature = `${selectedTenantId}|${shareInvitationId}`;
    openedDeepLinkSharesRef.current.add(signature);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'outbound');
    next.set('tenantId', selectedTenantId);
    next.set('shareInvitationId', shareInvitationId);
    next.delete('inboxItemId');
    setSearchParams(next, { replace: true });
    void loadShareThread(shareInvitationId);
  };

  useEffect(() => {
    if (activeTab !== 'outbound' || !selectedTenantId || !shareInvitationIdParam || loading) {
      return;
    }
    if (!shares.some((share) => share.shareInvitationId === shareInvitationIdParam)) {
      setError('This sent-work link is no longer available or you do not have permission to open it.');
      return;
    }
    const signature = `${selectedTenantId}|${shareInvitationIdParam}`;
    if (openedDeepLinkSharesRef.current.has(signature)) {
      return;
    }
    openedDeepLinkSharesRef.current.add(signature);

    void loadShareThread(shareInvitationIdParam);
  }, [activeTab, loading, selectedTenantId, shareInvitationIdParam, shares]);

  const loadRecipientThread = async (inboxItemId: string): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setRecipientThreadLoadingInboxItemId(inboxItemId);
    setError(null);
    try {
      const payload = await loadCollaborationRecipientThread({
        tenantId: selectedTenantId,
        inboxItemId
      });
      const preservedWorkItemId =
        recipientSyncPackageForm.workItemId && payload.workItems.some((entry) => entry.workItemId === recipientSyncPackageForm.workItemId)
          ? recipientSyncPackageForm.workItemId
          : payload.workItems[0]?.workItemId || '';
      setSelectedRecipientInboxItemId(inboxItemId);
      setRecipientThreadSnapshot(payload);
      setRecipientThreadMessageBody('');
      setRecipientSourceUpdateFieldResolutions({});
      setRecipientSyncPackageForm(
        preservedWorkItemId
          ? buildRecipientFieldUpdateFormForWorkItem(payload, preservedWorkItemId, tenant?.userEmail)
          : defaultRecipientSyncPackageFormState()
      );
      const item = inbox.find((entry) => entry.inboxItemId === inboxItemId);
      recordThreadSeen('received', inboxItemId, item ? receivedWorkActivityAt(item) : payload.thread.updatedAtUtc);
    } catch (threadError) {
      setError((threadError as Error).message);
    } finally {
      setRecipientThreadLoadingInboxItemId('');
    }
  };

  const openRecipientThread = (inboxItemId: string): void => {
    if (!selectedTenantId) return;
    const signature = `${selectedTenantId}|${inboxItemId}`;
    openedDeepLinkInboxItemsRef.current.add(signature);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'inbound');
    next.set('tenantId', selectedTenantId);
    next.set('inboxItemId', inboxItemId);
    next.delete('shareInvitationId');
    setSearchParams(next, { replace: true });
    void loadRecipientThread(inboxItemId);
  };

  useEffect(() => {
    if (activeTab !== 'inbound' || !selectedTenantId || !inboxItemIdParam || loading) return;
    if (!inbox.some((item) => item.inboxItemId === inboxItemIdParam)) {
      setError('This received-work link is no longer available or you do not have permission to open it.');
      return;
    }
    const signature = `${selectedTenantId}|${inboxItemIdParam}`;
    if (openedDeepLinkInboxItemsRef.current.has(signature)) return;
    openedDeepLinkInboxItemsRef.current.add(signature);
    void loadRecipientThread(inboxItemIdParam);
  }, [activeTab, inbox, inboxItemIdParam, loading, selectedTenantId]);

  const updateRecipientSelectedWorkItem = (workItemId: string): void => {
    if (!recipientThreadSnapshot) {
      setRecipientSyncPackageForm((current) => ({ ...current, workItemId }));
      return;
    }
    setRecipientSyncPackageForm(
      workItemId
        ? buildRecipientFieldUpdateFormForWorkItem(recipientThreadSnapshot, workItemId, tenant?.userEmail)
        : defaultRecipientSyncPackageFormState()
    );
  };

  const applyRecipientSourceUpdateFieldResolution = (
    syncPackageId: string,
    fieldKey: CollaborationEditableFieldKey,
    mode: CollaborationSourceUpdateFieldResolutionMode
  ): void => {
    if (!selectedRecipientPendingSourceUpdate || selectedRecipientPendingSourceUpdate.syncPackageId !== syncPackageId) {
      return;
    }
    const selectedWorkItem = recipientThreadSnapshot?.workItems.find(
      (entry) => entry.workItemId === selectedRecipientPendingSourceUpdate.workItemId
    );
    const sourceUpdateField =
      selectedWorkItem?.objectType === 'action'
        ? getActionSourceUpdateReviewFields(selectedRecipientPendingSourceUpdate, recipientSyncPackageForm).find(
            (entry) => entry.fieldKey === fieldKey
          )
        : getFindingSourceUpdateReviewFields(selectedRecipientPendingSourceUpdate, recipientSyncPackageForm).find(
            (entry) => entry.fieldKey === fieldKey
          );
    if (!sourceUpdateField) {
      return;
    }

    const currentFieldValue =
      selectedWorkItem?.objectType === 'action'
        ? recipientSyncPackageForm.actionFieldUpdates[fieldKey as 'title' | 'status' | 'ownerEmail' | 'targetDate']
        : recipientSyncPackageForm.findingFieldUpdates[fieldKey as 'description' | 'reference' | 'rootCause' | 'evidence'];
    let nextValue = currentFieldValue;
    if (mode === 'use_source') {
      nextValue = sourceUpdateField.sourceValue;
    } else if (mode === 'keep_mine') {
      nextValue = String(currentFieldValue || '').trim() || sourceUpdateField.previousValue;
    } else if (mode === 'manual_merge') {
      nextValue = String(currentFieldValue || '').trim() || sourceUpdateField.draftValue || sourceUpdateField.sourceValue;
    }

    setRecipientSyncPackageForm((current) => ({
      ...current,
      packageType: 'field_update',
      workItemId: selectedRecipientPendingSourceUpdate.workItemId,
      findingFieldUpdates:
        selectedWorkItem?.objectType === 'action'
          ? current.findingFieldUpdates
          : {
              ...current.findingFieldUpdates,
              [fieldKey]: nextValue
            },
      actionFieldUpdates:
        selectedWorkItem?.objectType === 'action'
          ? {
              ...current.actionFieldUpdates,
              [fieldKey]: nextValue
            }
          : current.actionFieldUpdates
    }));
    setRecipientSourceUpdateFieldResolutions((current) => ({
      ...current,
      [syncPackageId]: {
        ...(current[syncPackageId] || {}),
        [fieldKey]: mode
      }
    }));
  };

  const saveRecipientSyncPackageDraft = async (): Promise<void> => {
    if (!selectedRecipientInboxItemId || !recipientThreadSnapshot) {
      return;
    }
    if (recipientSyncPackageForm.packageType !== 'field_update') {
      setError('Draft save is currently available for governed field updates only.');
      return;
    }
    if (!recipientSyncPackageForm.workItemId) {
      setError('A work item is required.');
      return;
    }
    if (recipientSyncPackageForm.files.length > 0) {
      setError('Draft save does not include returned files. Clear selected files before saving the draft.');
      return;
    }
    if (!recipientSyncPackageFormHasFieldUpdateContent(recipientSyncPackageForm)) {
      setError('Add a governed field change or summary before saving a draft.');
      return;
    }

    const workItem = recipientThreadSnapshot.workItems.find(
      (entry) => entry.workItemId === recipientSyncPackageForm.workItemId
    );
    const blockedReason = getWorkItemSubmissionBlockReason(workItem);
    if (blockedReason) {
      setError(blockedReason);
      return;
    }
    if (!workItem?.lastOriginVersionToken) {
      setError('The selected work item does not yet have an origin version token. Refresh the thread first.');
      return;
    }
    const fieldSnapshot =
      workItem.objectType === 'action'
        ? recipientThreadSnapshot.fieldUpdateContext?.actionFieldContextsByObjectId[workItem.objectId]?.actionFieldSnapshot
        : recipientThreadSnapshot.fieldUpdateContext?.findingFieldSnapshot;

    setSavingRecipientSyncPackageDraft(true);
    setNotice(null);
    setError(null);
    try {
      await submitCollaborationRecipientSyncPackage({
        tenantId: selectedTenantId,
        inboxItemId: selectedRecipientInboxItemId,
        workItemId: recipientSyncPackageForm.workItemId,
        packageType: 'field_update',
        proposal:
          workItem.objectType === 'action'
            ? buildRecipientFieldUpdateProposalFromForm(
                recipientSyncPackageForm,
                'action',
                fieldSnapshot as Partial<Record<CollaborationActionEditableFieldKey, string>> | null | undefined
              )
            : buildRecipientFieldUpdateProposalFromForm(
                recipientSyncPackageForm,
                'finding',
                fieldSnapshot as Partial<Record<CollaborationFindingEditableFieldKey, string>> | null | undefined
              ),
        originVersionToken: workItem.lastOriginVersionToken,
        uploads: [],
        submissionMode: 'draft'
      });
      setNotice('Field-update draft saved.');
      await loadRecipientThread(selectedRecipientInboxItemId);
    } catch (draftError) {
      setError((draftError as Error).message);
    } finally {
      setSavingRecipientSyncPackageDraft(false);
    }
  };

  const handleRecipientSyncPackageFormChange = (nextForm: RecipientSyncPackageFormState): void => {
    if (
      selectedRecipientPendingSourceUpdate &&
      nextForm.packageType === 'field_update' &&
      nextForm.workItemId === selectedRecipientPendingSourceUpdate.workItemId
    ) {
      const sourceUpdateFieldKeys = new Set(
        (
          selectedRecipientWorkItem?.objectType === 'action'
            ? getActionSourceUpdateReviewFields(selectedRecipientPendingSourceUpdate, nextForm)
            : getFindingSourceUpdateReviewFields(selectedRecipientPendingSourceUpdate, nextForm)
        ).map((entry) => entry.fieldKey)
      );
      if (sourceUpdateFieldKeys.size > 0) {
        setRecipientSourceUpdateFieldResolutions((current) => {
          const nextResolutions = { ...(current[selectedRecipientPendingSourceUpdate.syncPackageId] || {}) };
          sourceUpdateFieldKeys.forEach((fieldKey) => {
            const normalizedValue =
              selectedRecipientWorkItem?.objectType === 'action'
                ? String(nextForm.actionFieldUpdates[fieldKey as 'title' | 'status' | 'ownerEmail' | 'targetDate'] || '').trim()
                : String(nextForm.findingFieldUpdates[fieldKey as 'description' | 'reference' | 'rootCause' | 'evidence'] || '').trim();
            if (normalizedValue.length > 0 && !nextResolutions[fieldKey]) {
              nextResolutions[fieldKey] = 'manual_merge';
            }
          });
          return {
            ...current,
            [selectedRecipientPendingSourceUpdate.syncPackageId]: nextResolutions
          };
        });
      }
    }
    setRecipientSyncPackageForm(nextForm);
  };

  const closeThread = (): void => {
    setSelectedThreadShareId('');
    setThreadSnapshot(null);
    setThreadMessageBody('');
    setSyncPackageReviewNotes({});
    const next = new URLSearchParams(searchParams);
    next.delete('shareInvitationId');
    setSearchParams(next, { replace: true });
    window.requestAnimationFrame(() => document.getElementById('sent-work-heading')?.focus());
  };

  const closeRecipientThread = (): void => {
    setSelectedRecipientInboxItemId('');
    setRecipientThreadSnapshot(null);
    setRecipientThreadMessageBody('');
    setRecipientSourceUpdateFieldResolutions({});
    setRecipientSyncPackageForm(defaultRecipientSyncPackageFormState());
    const next = new URLSearchParams(searchParams);
    next.delete('inboxItemId');
    setSearchParams(next, { replace: true });
    window.requestAnimationFrame(() => document.getElementById('received-work-heading')?.focus());
  };

  useEffect(() => {
    if (!threadSnapshot || !selectedThreadShareId) return;
    window.requestAnimationFrame(() => document.getElementById('sent-thread-heading')?.focus());
  }, [selectedThreadShareId, threadSnapshot]);

  useEffect(() => {
    if (!recipientThreadSnapshot || !selectedRecipientInboxItemId) return;
    window.requestAnimationFrame(() => document.getElementById('received-thread-heading')?.focus());
  }, [recipientThreadSnapshot, selectedRecipientInboxItemId]);

  const postThreadMessage = async (): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    const body = threadMessageBody.trim();
    if (!body) {
      setError('Thread message is required.');
      return;
    }
    setPostingThreadMessage(true);
    setNotice(null);
    setError(null);
    try {
      await postCollaborationShareThreadMessage({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        body
      });
      setNotice('Thread message posted.');
      setThreadMessageBody('');
      await loadShareThread(selectedThreadShareId);
    } catch (threadError) {
      setError((threadError as Error).message);
    } finally {
      setPostingThreadMessage(false);
    }
  };

  const describeTranslationOutcome = (result: {
    translation: { translatedBody: string };
    persisted: boolean;
    reused: boolean;
    warning?: string;
  }): string => {
    if (!result.persisted) {
      return `${result.warning ?? 'The translation could not be stored.'} Translation: ${result.translation.translatedBody}`;
    }
    return result.reused
      ? 'A current translation already existed and was reused; no characters were metered.'
      : 'Message translated. The original is kept beside the translation.';
  };

  const translateThreadMessage = async (messageId: string, targetLanguage: string, force?: boolean): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    setTranslatingMessageId(messageId);
    setNotice(null);
    setError(null);
    try {
      const result = await translateCollaborationShareThreadMessage({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        messageId,
        targetLanguage,
        force
      });
      setNotice(describeTranslationOutcome(result));
      await loadShareThread(selectedThreadShareId);
    } catch (translateError) {
      setError((translateError as Error).message);
    } finally {
      setTranslatingMessageId('');
    }
  };

  const translateRecipientThreadMessage = async (messageId: string, targetLanguage: string, force?: boolean): Promise<void> => {
    if (!selectedRecipientInboxItemId) {
      return;
    }
    setTranslatingMessageId(messageId);
    setNotice(null);
    setError(null);
    try {
      const result = await translateCollaborationRecipientThreadMessage({
        tenantId: selectedTenantId,
        inboxItemId: selectedRecipientInboxItemId,
        messageId,
        targetLanguage,
        force
      });
      setNotice(describeTranslationOutcome(result));
      await loadRecipientThread(selectedRecipientInboxItemId);
    } catch (translateError) {
      setError((translateError as Error).message);
    } finally {
      setTranslatingMessageId('');
    }
  };

  const postRecipientThreadMessage = async (): Promise<void> => {
    if (!selectedRecipientInboxItemId) {
      return;
    }
    const body = recipientThreadMessageBody.trim();
    if (!body) {
      setError('Thread message is required.');
      return;
    }
    setPostingRecipientThreadMessage(true);
    setNotice(null);
    setError(null);
    try {
      await postCollaborationRecipientThreadMessage({
        tenantId: selectedTenantId,
        inboxItemId: selectedRecipientInboxItemId,
        body
      });
      setNotice('Thread message posted.');
      setRecipientThreadMessageBody('');
      await loadRecipientThread(selectedRecipientInboxItemId);
    } catch (threadError) {
      setError((threadError as Error).message);
    } finally {
      setPostingRecipientThreadMessage(false);
    }
  };

  const submitRecipientSyncPackage = async (): Promise<void> => {
    if (!selectedRecipientInboxItemId || !recipientThreadSnapshot) {
      return;
    }
    if (!recipientSyncPackageForm.workItemId) {
      setError('A work item is required.');
      return;
    }
    if (
      recipientSyncPackageForm.packageType === 'field_update' &&
      !recipientSyncPackageFormHasFieldUpdateContent(recipientSyncPackageForm)
    ) {
      setError('At least one governed field change is required.');
      return;
    }
    if (recipientSyncPackageForm.packageType === 'action_proposal' && !recipientSyncPackageForm.actionProposalTitle.trim()) {
      setError('Action proposal title is required.');
      return;
    }

    const workItem = recipientThreadSnapshot.workItems.find(
      (entry) => entry.workItemId === recipientSyncPackageForm.workItemId
    );
    const blockedReason = getWorkItemSubmissionBlockReason(workItem);
    if (blockedReason) {
      setError(blockedReason);
      return;
    }
    if (!workItem?.lastOriginVersionToken) {
      setError('The selected work item does not yet have an origin version token. Refresh the thread first.');
      return;
    }
    const fieldSnapshot =
      workItem.objectType === 'action'
        ? recipientThreadSnapshot.fieldUpdateContext?.actionFieldContextsByObjectId[workItem.objectId]?.actionFieldSnapshot
        : recipientThreadSnapshot.fieldUpdateContext?.findingFieldSnapshot;

    setSubmittingRecipientSyncPackage(true);
    setNotice(null);
    setError(null);
    try {
      // Each file is uploaded on its own first (streamed, never base64 in
      // JSON); the package then names the uploads.
      const uploads: Array<{ uploadId: string; isRequiredForReview: boolean }> = [];
      for (const file of recipientSyncPackageForm.files) {
        const { uploadId } = await uploadCollaborationRecipientFile({
          tenantId: selectedTenantId,
          inboxItemId: selectedRecipientInboxItemId,
          file
        });
        uploads.push({ uploadId, isRequiredForReview: recipientSyncPackageForm.filesRequired });
      }
      const proposal =
        recipientSyncPackageForm.packageType === 'action_proposal'
          ? {
              summary: recipientSyncPackageForm.summary.trim(),
              actionProposal: {
                title: recipientSyncPackageForm.actionProposalTitle.trim(),
                description: recipientSyncPackageForm.actionProposalDescription.trim() || null,
                ownerEmail: recipientSyncPackageForm.actionProposalOwnerEmail.trim() || null,
                targetDate: recipientSyncPackageForm.actionProposalTargetDate.trim() || null,
                status: recipientSyncPackageForm.actionProposalStatus.trim() || null,
                verificationPersonEmail:
                  recipientSyncPackageForm.actionProposalVerificationPersonEmail.trim() || null,
                verificationNotes:
                  recipientSyncPackageForm.actionProposalVerificationNotes.trim() || null,
                mitigatesFindingRisk: recipientSyncPackageForm.actionProposalMitigatesRisk
              }
            }
          : recipientSyncPackageForm.packageType === 'field_update'
            ? workItem.objectType === 'action'
              ? buildRecipientFieldUpdateProposalFromForm(
                  recipientSyncPackageForm,
                  'action',
                  fieldSnapshot as Partial<Record<CollaborationActionEditableFieldKey, string>> | null | undefined
                )
              : buildRecipientFieldUpdateProposalFromForm(
                  recipientSyncPackageForm,
                  'finding',
                  fieldSnapshot as Partial<Record<CollaborationFindingEditableFieldKey, string>> | null | undefined
                )
            : {
                summary: recipientSyncPackageForm.summary.trim()
              };

      await submitCollaborationRecipientSyncPackage({
        tenantId: selectedTenantId,
        inboxItemId: selectedRecipientInboxItemId,
        workItemId: recipientSyncPackageForm.workItemId,
        packageType: recipientSyncPackageForm.packageType,
        proposal,
        originVersionToken: workItem.lastOriginVersionToken,
        uploads
      });
      setNotice('Sync package submitted.');
      setRecipientSyncPackageForm((current) => ({
        ...defaultRecipientSyncPackageFormState(),
        workItemId: current.workItemId
      }));
      await loadRecipientThread(selectedRecipientInboxItemId);
    } catch (submissionError) {
      setError((submissionError as Error).message);
    } finally {
      setSubmittingRecipientSyncPackage(false);
    }
  };

  const withdrawRecipientSyncPackage = async (syncPackageId: string): Promise<void> => {
    if (!selectedRecipientInboxItemId) {
      return;
    }
    setWithdrawingRecipientSyncPackageId(syncPackageId);
    setNotice(null);
    setError(null);
    try {
      await withdrawCollaborationRecipientSyncPackage({
        tenantId: selectedTenantId,
        inboxItemId: selectedRecipientInboxItemId,
        syncPackageId,
        reason: 'Supplier withdrew this sync package before owner review.'
      });
      setNotice('Sync package withdrawn. A fresh package can now be submitted when ready.');
      await loadRecipientThread(selectedRecipientInboxItemId);
    } catch (withdrawError) {
      setError((withdrawError as Error).message);
    } finally {
      setWithdrawingRecipientSyncPackageId('');
    }
  };

  const acknowledgeRecipientSourceUpdate = async (syncPackageId: string): Promise<void> => {
    if (!selectedRecipientInboxItemId || !recipientThreadSnapshot) {
      return;
    }
    setAcknowledgingRecipientSourceUpdateId(syncPackageId);
    setNotice(null);
    setError(null);
    try {
      const syncPackage = recipientThreadSnapshot.syncPackages.find((entry) => entry.syncPackageId === syncPackageId);
      const sourceUpdateMode = syncPackage?.proposalJson
        ? extractSourceUpdateMode(parseJsonSafe(syncPackage.proposalJson))
        : null;
      if (
        sourceUpdateMode === 'published_new_baseline' &&
        syncPackage &&
        selectedRecipientPendingSourceUpdate?.syncPackageId === syncPackage.syncPackageId
      ) {
        const unresolvedFields = (
          selectedRecipientWorkItem?.objectType === 'action'
            ? getActionSourceUpdateReviewFields(syncPackage, recipientSyncPackageForm)
            : getFindingSourceUpdateReviewFields(syncPackage, recipientSyncPackageForm)
        )
          .filter(
            (entry) => !recipientSourceUpdateFieldResolutions[syncPackage.syncPackageId]?.[entry.fieldKey]
          )
          .map((entry) =>
            selectedRecipientWorkItem?.objectType === 'action'
              ? actionFieldUpdateLabel(entry.fieldKey as 'title' | 'status' | 'ownerEmail' | 'targetDate')
              : findingFieldUpdateLabel(entry.fieldKey as 'description' | 'reference' | 'rootCause' | 'evidence')
          );
        if (unresolvedFields.length > 0) {
          throw new Error(
            `Review every source-changed governed field before acknowledging the new baseline: ${unresolvedFields.join(', ')}.`
          );
        }
      }
      const rebasedDraft =
        syncPackage && sourceUpdateMode === 'published_new_baseline'
          ? selectedRecipientWorkItem?.objectType === 'action'
            ? buildRebasedActionDraftFromForm(
                recipientThreadSnapshot,
                syncPackage.workItemId,
                recipientSyncPackageForm,
                tenant?.userEmail
              )
            : buildRebasedFindingDraftFromForm(
                recipientThreadSnapshot,
                syncPackage.workItemId,
                recipientSyncPackageForm,
                tenant?.userEmail
              )
          : undefined;
      await acknowledgeCollaborationRecipientSourceUpdate({
        tenantId: selectedTenantId,
        inboxItemId: selectedRecipientInboxItemId,
        syncPackageId,
        rebasedDraft
      });
      setNotice(
        sourceUpdateMode === 'published_new_baseline'
          ? rebasedDraft
            ? 'Source update acknowledged and your rebased draft was saved.'
            : 'Source baseline accepted. Supplier quality work can continue on the refreshed work item.'
          : 'Source update acknowledged. Supplier quality work can continue on the refreshed baseline.'
      );
      await loadRecipientThread(selectedRecipientInboxItemId);
    } catch (ackError) {
      setError((ackError as Error).message);
    } finally {
      setAcknowledgingRecipientSourceUpdateId('');
    }
  };

  const updateWorkItemState = async (workItemId: string, targetState: 'active' | 'recalled'): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    setUpdatingWorkItemStateId(workItemId);
    setNotice(null);
    setError(null);
    try {
      const payload = await updateCollaborationWorkItemState({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        workItemId,
        targetState,
        reason:
          targetState === 'recalled'
            ? 'The owner recalled this work item from active supplier quality work.'
            : 'The owner reopened this work item for active supplier quality work.'
      });
      if (targetState === 'recalled') {
        setNotice(
          payload.stalePackageCount && payload.stalePackageCount > 0
            ? `Work item recalled. ${payload.stalePackageCount} active sync package${payload.stalePackageCount === 1 ? '' : 's'} marked stale so supplier quality work can resume cleanly if reopened later.`
            : 'Work item recalled from active supplier quality work.'
        );
      } else {
        setNotice('Work item reopened for supplier quality submissions.');
      }
      await loadShareThread(selectedThreadShareId);
    } catch (stateError) {
      setError((stateError as Error).message);
    } finally {
      setUpdatingWorkItemStateId('');
    }
  };

  const withdrawRecipientReturnedActionProposal = async (returnedActionProposalId: string): Promise<void> => {
    if (!selectedRecipientInboxItemId) {
      return;
    }
    setWithdrawingRecipientReturnedActionProposalId(returnedActionProposalId);
    setNotice(null);
    setError(null);
    try {
      await withdrawCollaborationReturnedActionProposal({
        tenantId: selectedTenantId,
        inboxItemId: selectedRecipientInboxItemId,
        returnedActionProposalId
      });
      setNotice('Returned Action proposal withdrawn.');
      await loadRecipientThread(selectedRecipientInboxItemId);
    } catch (withdrawError) {
      setError((withdrawError as Error).message);
    } finally {
      setWithdrawingRecipientReturnedActionProposalId('');
    }
  };

  const downloadRecipientDeliveredEvidence = async (artifactId: string, fileName: string): Promise<void> => {
    if (!selectedRecipientInboxItemId) {
      return;
    }
    setDownloadingRecipientArtifactId(artifactId);
    setNotice(null);
    setError(null);
    try {
      await downloadRecipientCollaborationArtifact({
        tenantId: selectedTenantId,
        inboxItemId: selectedRecipientInboxItemId,
        artifactId,
        fileName
      });
      setNotice('Customer evidence file downloaded after integrity verification.');
    } catch (downloadError) {
      setError((downloadError as Error).message);
    } finally {
      setDownloadingRecipientArtifactId('');
    }
  };

  const reviewSyncPackage = async (
    syncPackageId: string,
    decision: 'accepted' | 'rejected'
  ): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    setReviewingSyncPackageId(syncPackageId);
    setNotice(null);
    setError(null);
    try {
      const reviewNote = String(syncPackageReviewNotes[syncPackageId] || '').trim();
      const payload = await reviewCollaborationSyncPackage({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        syncPackageId,
        decision,
        reviewNote
      });
      if (payload.httpStatus === 409 && payload.status === 'stale_conflict') {
        setSyncPackageReviewNotes((current) => {
          const next = { ...current };
          delete next[syncPackageId];
          return next;
        });
        setError(
          payload.message ??
            'This sync package became stale because the origin work item changed before review. It has been marked stale and a fresh package can now be submitted.'
        );
        await loadShareThread(selectedThreadShareId);
        return;
      }
      setSyncPackageReviewNotes((current) => {
        const next = { ...current };
        delete next[syncPackageId];
        return next;
      });
      setNotice(`Sync package ${decision}.`);
      await loadShareThread(selectedThreadShareId);
    } catch (reviewError) {
      setError((reviewError as Error).message);
    } finally {
      setReviewingSyncPackageId('');
    }
  };

  const withdrawSyncPackage = async (syncPackageId: string): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    setWithdrawingSyncPackageId(syncPackageId);
    setNotice(null);
    setError(null);
    try {
      await withdrawCollaborationSyncPackage({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        syncPackageId,
        reason:
          syncPackageReviewNotes[syncPackageId]?.trim() ||
          'Owner superseded this sync package before review.'
      });
      setSyncPackageReviewNotes((current) => {
        const next = { ...current };
        delete next[syncPackageId];
        return next;
      });
      setNotice('Sync package superseded. It no longer blocks fresh supplier submission.');
      await loadShareThread(selectedThreadShareId);
    } catch (withdrawError) {
      setError((withdrawError as Error).message);
    } finally {
      setWithdrawingSyncPackageId('');
    }
  };

  const refreshWorkItemOriginVersion = async (workItemId: string): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    setRefreshingWorkItemId(workItemId);
    setNotice(null);
    setError(null);
    try {
      const payload = await refreshCollaborationWorkItemOriginVersion({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        workItemId,
        reason: 'Origin record changed outside the supplier quality sync package flow.'
      });
      setNotice(
        payload.stalePackageCount && payload.stalePackageCount > 0
          ? `Origin version token refreshed. ${payload.stalePackageCount} stale sync package${payload.stalePackageCount === 1 ? '' : 's'} marked stale so suppliers can resubmit.`
          : 'Origin version token refreshed for the selected work item.'
      );
      await loadShareThread(selectedThreadShareId);
    } catch (refreshError) {
      setError((refreshError as Error).message);
    } finally {
      setRefreshingWorkItemId('');
    }
  };

  const refreshAllWorkItemOriginVersions = async (): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    setRefreshingAllWorkItems(true);
    setNotice(null);
    setError(null);
    try {
      const payload = await refreshAllCollaborationWorkItemOriginVersions({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        reason: 'Origin records changed outside the supplier quality sync package flow.'
      });
      setNotice(
        payload.stalePackageCount && payload.stalePackageCount > 0
          ? `Refreshed ${payload.refreshedWorkItemCount} work item${payload.refreshedWorkItemCount === 1 ? '' : 's'}. ${payload.stalePackageCount} stale sync package${payload.stalePackageCount === 1 ? '' : 's'} marked stale so suppliers can resubmit.`
          : `Refreshed ${payload.refreshedWorkItemCount} work item${payload.refreshedWorkItemCount === 1 ? '' : 's'} across the thread.`
      );
      await loadShareThread(selectedThreadShareId);
    } catch (refreshError) {
      setError((refreshError as Error).message);
    } finally {
      setRefreshingAllWorkItems(false);
    }
  };

  const releaseArtifact = async (artifactId: string): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    setReleasingArtifactId(artifactId);
    setNotice(null);
    setError(null);
    try {
      await releaseCollaborationArtifact({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        artifactId
      });
      setNotice('Artifact released from quarantine. Any blocked sync package has been recalculated.');
      await loadShareThread(selectedThreadShareId);
    } catch (releaseError) {
      setError((releaseError as Error).message);
    } finally {
      setReleasingArtifactId('');
    }
  };

  const downloadArtifact = async (artifactId: string): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    const artifact = threadSnapshot?.artifacts.find((entry) => entry.artifactId === artifactId);
    setDownloadingArtifactId(artifactId);
    setNotice(null);
    setError(null);
    try {
      await downloadCollaborationArtifact({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        artifactId,
        fileName: artifact?.fileName || 'collaboration-artifact'
      });
      setNotice('Artifact downloaded. The download was recorded in the collaboration activity trail.');
      await loadShareThread(selectedThreadShareId);
    } catch (downloadError) {
      setError((downloadError as Error).message);
    } finally {
      setDownloadingArtifactId('');
    }
  };

  const purgeArtifactBinary = async (artifactId: string): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    if (!window.confirm('Purge the stored binary for this supplier quality artifact? Metadata and history will remain.')) {
      return;
    }
    setPurgingArtifactId(artifactId);
    setNotice(null);
    setError(null);
    try {
      const payload = await purgeCollaborationArtifactBinary({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        artifactId,
        reason: 'Owner-requested collaboration artifact binary purge from portal thread.'
      });
      setNotice(
        payload.purgedArtifactCount > 0
          ? 'Artifact binary purged. Metadata and supplier quality history remain available.'
          : 'Artifact binary was already purged.'
      );
      await loadShareThread(selectedThreadShareId);
    } catch (purgeError) {
      setError((purgeError as Error).message);
    } finally {
      setPurgingArtifactId('');
    }
  };

  const purgeSyncPackageArtifacts = async (syncPackageId: string): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    if (!window.confirm('Purge all stored binaries for this sync package? Metadata and history will remain.')) {
      return;
    }
    setPurgingSyncPackageArtifactsId(syncPackageId);
    setNotice(null);
    setError(null);
    try {
      const payload = await purgeCollaborationSyncPackageArtifacts({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        syncPackageId,
        reason: 'Owner-requested collaboration sync package artifact purge from portal thread.'
      });
      setNotice(
        payload.purgedArtifactCount > 0
          ? `Purged ${payload.purgedArtifactCount} sync package artifact ${payload.purgedArtifactCount === 1 ? 'binary' : 'binaries'}. Metadata and history remain available.`
          : 'All artifact binaries for this sync package were already purged.'
      );
      await loadShareThread(selectedThreadShareId);
    } catch (purgeError) {
      setError((purgeError as Error).message);
    } finally {
      setPurgingSyncPackageArtifactsId('');
    }
  };

  const purgeAllThreadArtifacts = async (): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    if (!window.confirm('Purge all stored binaries for this supplier quality thread? Metadata and history will remain.')) {
      return;
    }
    setPurgingAllThreadArtifacts(true);
    setNotice(null);
    setError(null);
    try {
      const payload = await purgeCollaborationThreadArtifacts({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        reason: 'Owner-requested full collaboration thread artifact purge from portal thread.'
      });
      setNotice(
        payload.purgedArtifactCount > 0
          ? `Purged ${payload.purgedArtifactCount} thread artifact ${payload.purgedArtifactCount === 1 ? 'binary' : 'binaries'}. Metadata and history remain available.`
          : 'All artifact binaries in this supplier quality thread were already purged.'
      );
      await loadShareThread(selectedThreadShareId);
    } catch (purgeError) {
      setError((purgeError as Error).message);
    } finally {
      setPurgingAllThreadArtifacts(false);
    }
  };

  const decideReturnedActionProposal = async (
    returnedActionProposalId: string,
    decision: 'adopted' | 'declined'
  ): Promise<void> => {
    if (!selectedThreadShareId) {
      return;
    }
    setDecidingReturnedActionProposalId(returnedActionProposalId);
    setNotice(null);
    setError(null);
    try {
      await decideCollaborationReturnedActionProposal({
        tenantId: selectedTenantId,
        shareInvitationId: selectedThreadShareId,
        returnedActionProposalId,
        decision
      });
      setNotice(
        decision === 'adopted'
          ? 'Returned Action proposal marked adopted.'
          : 'Returned Action proposal declined.'
      );
      await loadShareThread(selectedThreadShareId);
    } catch (decisionError) {
      setError((decisionError as Error).message);
    } finally {
      setDecidingReturnedActionProposalId('');
    }
  };

  return (
    <div>
      <PortalPageHeader
        title="Supplier Quality Collaboration"
        description={canManage
          ? 'Set up suppliers and manage the quality work exchanged with them.'
          : 'Your quality work with suppliers: collaborations you started, or whose Finding or Audit you lead.'}
        actions={(
          <span className="collaboration-inline-actions">
            <CollaborationStartButton targets={startTargets} />
            <button className="btn secondary" type="button" onClick={() => void loadAll()} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </span>
        )}
        helpHref="/docs?path=docs/documentation/users/guide-supplier-collaboration-journey.md"
        helpLabel="How supplier collaboration works"
        context={(
          <div className="card">
            <AdminTenantSelector
              label="Customer tenant"
              value={selectedTenantId}
              options={deployableTenants}
              onChange={(tenantId) => {
                setSelectedTenantId(tenantId);
                const next = new URLSearchParams(searchParams);
                next.set('tenantId', tenantId);
                next.delete('shareInvitationId');
                next.delete('inboxItemId');
                setSearchParams(next, { replace: true });
              }}
              helperText={canManage
                ? 'Supplier setup and exchanged work are managed for one customer tenant at a time.'
                : 'Shows your collaborations in one tenant at a time. Other tenants show only collaborations you manage there.'}
            />
          </div>
        )}
      />

      {activeTab === 'outbound' && !canInitiateShare && (
        <Notice kind="warning" marginTop={10}>
          Sending supplier work requires an active Edinshall plan.
        </Notice>
      )}

      {activeTab === 'outbound' && isTextOnlyShare && shareAllowance?.allowance && (
        <Notice kind="neutral" marginTop={10}>
          Your plan sends Findings to suppliers as text only — evidence files are not sent, and suppliers reply
          in text. {shareAllowance.allowance.usedThisPeriod} of {shareAllowance.allowance.included} new
          collaborations used this month.
          {shareAllowance.allowance.remaining === 0 && !shareAllowance.allowance.allowShareOverage && (
            <> You can still reply in existing collaborations. New ones are available again next month, or with a
            Standard or Enterprise plan.</>
          )}
          {/* The overage switch appears only once Free metering is enabled (tracker #117). */}
          {canManage && shareAllowance.allowance.meteringEnabled && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
              <input
                type="checkbox"
                checked={shareAllowance.allowance.allowShareOverage}
                disabled={shareOverageSaving}
                onChange={(event) => void toggleShareOverage(event.target.checked)}
              />{' '}
              Allow new collaborations beyond the monthly allowance, billed per collaboration through Microsoft
              Marketplace.
            </label>
          )}
        </Notice>
      )}

      {notice && <Notice kind="success" marginTop={10}>{notice}</Notice>}
      {error && <Notice kind="warning" marginTop={10}>{error}</Notice>}

      <nav aria-label="Supplier collaboration sections" className="collaboration-tabs">
          {visibleTabs.map((tab) => {
            const selected = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                aria-current={selected ? 'page' : undefined}
                onClick={() => setActiveTab(tab.key)}
                className={`btn collaboration-tab${selected ? '' : ' secondary'}`}
              >
                <span>{tab.label}</span>
                {tab.key === 'outbound' && newSentShareIds.size > 0 && (
                  <span className="collaboration-tab__count" aria-label={`${newSentShareIds.size} new`}>
                    {newSentShareIds.size}
                  </span>
                )}
                {tab.key === 'inbound' && newInboxItemIds.size > 0 && (
                  <span className="collaboration-tab__count" aria-label={`${newInboxItemIds.size} new`}>
                    {newInboxItemIds.size}
                  </span>
                )}
              </button>
            );
          })}
      </nav>

      {loading && (
        <PortalDataState
          kind="loading"
          heading="Loading supplier collaboration"
          message="Checking supplier setup and exchanged work for this tenant."
        />
      )}

      {!loading && activeTab === 'overview' && (
        <CollaborationOverview
          model={overviewModel}
          selectedTenantId={selectedTenantId}
          invitationEvidenceIncomplete={overviewInvitationEvidenceIncomplete}
          onNavigate={setActiveTab}
          canManage={canManage}
        />
      )}

      {!loading && canManage && (activeTab === 'partners' || activeTab === 'settings') && (
        <div
          className={activeTab === 'partners' ? '' : 'collaboration-configuration-stack'}
          style={{ marginTop: '12px' }}
        >
          {activeTab === 'settings' && (
            <CollaborationPolicyCard
            canManage={canManage}
            policy={policy}
            policyForm={policyForm}
            savingPolicy={savingPolicy}
            policyLabel={policyLabel}
            relativeTime={relativeTime}
            onRecipientPolicyChange={(value) =>
              setPolicyForm((current) => ({
                ...current,
                recipientPolicy: value
              }))
            }
            onAllowUnregisteredViewChange={(value) =>
              setPolicyForm((current) => ({ ...current, allowUnregisteredView: value }))
            }
            onRequireVerifiedEmailChange={(value) =>
              setPolicyForm((current) => ({ ...current, requireVerifiedEmail: value }))
            }
            onSavePolicy={() => void savePolicy()}
          />
          )}

          {activeTab === 'partners' && (
          <CollaborationPartnersCard
            canManage={canManage}
            selectedTenantId={selectedTenantId}
            partnerForm={partnerForm}
            partners={partners}
            savingPartnerId={savingPartnerId}
            supplierImportFile={supplierImportFile}
            supplierImportBatch={supplierImportBatch}
            previewingSupplierImport={previewingSupplierImport}
            committingSupplierImport={committingSupplierImport}
            relativeTime={relativeTime}
            onPartnerFormChange={setPartnerForm}
            onCreatePartner={() => void createPartner()}
            onSupplierImportFileChange={setSupplierImportFile}
            onPreviewSupplierImport={() => void previewSupplierCsvImport()}
            onCommitSupplierImport={() => void commitSupplierCsvImport()}
          />
          )}

          {activeTab === 'settings' && (
          <CollaborationRuntimeCard
            canManage={canManage}
            tenantId={selectedTenantId}
            instances={instances}
            savingInstanceId={savingInstanceId}
            revokingParentBindingId={revokingParentBindingId}
            purgingTenantArtifacts={purgingTenantArtifacts}
            purgingInstanceArtifactsId={purgingInstanceArtifactsId}
            onSaveInstanceSettings={(instance, update) => void saveInstanceSettings(instance, update)}
            onRevokeParentBinding={(instance, parentBindingId) =>
              void revokeParentBinding(instance, parentBindingId)
            }
            onPurgeTenantArtifacts={() => void purgeTenantArtifacts()}
            onPurgeInstanceArtifacts={(instance) => void purgeInstanceArtifacts(instance)}
            onOpenConsumption={() => navigate('/account')}
          />
          )}
        </div>
      )}

      {!loading && (
        <>
          {activeTab === 'outbound' && !threadSnapshot && (
          <CollaborationSentSharesCard
            shares={shares}
            partnerNameById={partnerNameById}
            revokingShareId={revokingShareId}
            threadLoadingShareId={threadLoadingShareId}
            newShareIds={newSentShareIds}
            evidenceByShareId={sentEvidenceByShareId}
            relativeTime={relativeTime}
            parseJsonSafe={parseJsonSafe}
            onRevokeShare={(shareInvitationId) => void revokeShare(shareInvitationId)}
            onOpenThread={openShareThread}
          />
          )}

          {activeTab === 'outbound' && threadSnapshot && selectedThreadShareId && (
            <CollaborationOwnerThreadPane
              threadSnapshot={threadSnapshot}
              partnerNameById={partnerNameById}
              threadMessageBody={threadMessageBody}
              postingThreadMessage={postingThreadMessage}
              translatingMessageId={translatingMessageId}
              onTranslateMessage={translateThreadMessage}
              reviewingSyncPackageId={reviewingSyncPackageId}
              withdrawingSyncPackageId={withdrawingSyncPackageId}
              syncPackageReviewNotes={syncPackageReviewNotes}
              refreshingWorkItemId={refreshingWorkItemId}
              refreshingAllWorkItems={refreshingAllWorkItems}
              updatingWorkItemStateId={updatingWorkItemStateId}
              releasingArtifactId={releasingArtifactId}
              downloadingArtifactId={downloadingArtifactId}
              purgingArtifactId={purgingArtifactId}
              purgingSyncPackageArtifactsId={purgingSyncPackageArtifactsId}
              purgingAllThreadArtifacts={purgingAllThreadArtifacts}
              decidingReturnedActionProposalId={decidingReturnedActionProposalId}
              relativeTime={relativeTime}
              describeWorkItemState={describeWorkItemState}
              describeThreadEvent={describeThreadEvent}
              formatBytes={formatBytes}
              parseJsonSafe={parseJsonSafe}
              extractActionProposalDetails={extractActionProposalDetails}
              extractFindingFieldUpdates={extractFindingFieldUpdates}
              findingFieldUpdateLabel={findingFieldUpdateLabel}
              onCloseThread={closeThread}
              onRefreshAllWorkItemOriginVersions={() => void refreshAllWorkItemOriginVersions()}
              onRefreshWorkItemOriginVersion={(workItemId) => void refreshWorkItemOriginVersion(workItemId)}
              onUpdateWorkItemState={(workItemId, state) => void updateWorkItemState(workItemId, state)}
              onThreadMessageBodyChange={setThreadMessageBody}
              onPostThreadMessage={() => void postThreadMessage()}
              onSyncPackageReviewNoteChange={(syncPackageId, value) =>
                setSyncPackageReviewNotes((current) => ({
                  ...current,
                  [syncPackageId]: value
                }))
              }
              onReviewSyncPackage={(syncPackageId, decision) => void reviewSyncPackage(syncPackageId, decision)}
              onWithdrawSyncPackage={(syncPackageId) => void withdrawSyncPackage(syncPackageId)}
              onReleaseArtifact={(artifactId) => void releaseArtifact(artifactId)}
              onDownloadArtifact={(artifactId) => void downloadArtifact(artifactId)}
              onPurgeArtifactBinary={(artifactId) => void purgeArtifactBinary(artifactId)}
              onPurgeSyncPackageArtifacts={(syncPackageId) => void purgeSyncPackageArtifacts(syncPackageId)}
              onPurgeThreadArtifacts={() => void purgeAllThreadArtifacts()}
              canPurge={canManage}
              onDecideReturnedActionProposal={(proposalId, decision) =>
                void decideReturnedActionProposal(proposalId, decision)
              }
            />
          )}

          {activeTab === 'inbound' && !recipientThreadSnapshot && (
          <CollaborationInboxCard
            inbox={inbox}
            instanceById={instanceById}
            recipientThreadLoadingInboxItemId={recipientThreadLoadingInboxItemId}
            newInboxItemIds={newInboxItemIds}
            evidenceByInboxItemId={receivedEvidenceByInboxItemId}
            relativeTime={relativeTime}
            parseJsonSafe={parseJsonSafe}
            updatingRelationshipId={updatingInboundCustomerRelationshipId}
            editingRelationshipId={editingInboundCustomerRelationshipId}
            relationshipDisplayNameDraft={inboundCustomerDisplayNameDraft}
            savingRelationshipDisplayNameId={savingInboundCustomerDisplayNameId}
            triagingInboxItemId={triagingInboxItemId}
            baselineActionInboxItemId={baselineActionInboxItemId}
            onUpdateRelationshipStatus={(relationshipId, status) =>
              void updateInboundCustomerRelationship(relationshipId, status)
            }
            onStartEditRelationshipDisplayName={(relationshipId, currentDisplayName, rowVersion) =>
              startEditingInboundCustomerDisplayName(relationshipId, currentDisplayName, rowVersion)
            }
            onChangeRelationshipDisplayNameDraft={setInboundCustomerDisplayNameDraft}
            onCancelEditRelationshipDisplayName={cancelEditingInboundCustomerDisplayName}
            onSaveRelationshipDisplayName={(relationshipId) => void saveInboundCustomerDisplayName(relationshipId)}
            onTriageInboxItem={(inboxItemId, status) => void updateInboxItemTriage(inboxItemId, status)}
            onPullBaseline={(inboxItemId) => void pullInboxBaseline(inboxItemId)}
            onAcknowledgeBaseline={(inboxItemId) => void acknowledgeInboxBaseline(inboxItemId)}
            onOpenThread={openRecipientThread}
            canManage={canManage}
          />
          )}

          {activeTab === 'inbound' && recipientThreadSnapshot && selectedRecipientInboxItemId && (
            <CollaborationRecipientThreadPane
              threadSnapshot={recipientThreadSnapshot}
              selectedInboxRuntimeAccess={selectedRecipientInboxItem?.runtimeAccess ?? null}
              partnerNameById={partnerNameById}
              tenantUserEmail={tenant?.userEmail}
              recipientThreadMessageBody={recipientThreadMessageBody}
              postingRecipientThreadMessage={postingRecipientThreadMessage}
              translatingMessageId={translatingMessageId}
              onTranslateMessage={translateRecipientThreadMessage}
              recipientSyncPackageForm={recipientSyncPackageForm}
              selectedRecipientWorkItem={selectedRecipientWorkItem}
              selectedRecipientDraftSyncPackage={selectedRecipientDraftSyncPackage}
              selectedRecipientPendingSourceUpdate={selectedRecipientPendingSourceUpdate}
              selectedRecipientSourceUpdateReviewFields={selectedRecipientSourceUpdateReviewFields}
              selectedRecipientUnresolvedSourceUpdateFieldLabels={selectedRecipientUnresolvedSourceUpdateFieldLabels}
              submittingRecipientSyncPackage={submittingRecipientSyncPackage}
              savingRecipientSyncPackageDraft={savingRecipientSyncPackageDraft}
              withdrawingRecipientSyncPackageId={withdrawingRecipientSyncPackageId}
              acknowledgingRecipientSourceUpdateId={acknowledgingRecipientSourceUpdateId}
              withdrawingRecipientReturnedActionProposalId={withdrawingRecipientReturnedActionProposalId}
              downloadingRecipientArtifactId={downloadingRecipientArtifactId}
              relativeTime={relativeTime}
              formatBytes={formatBytes}
              parseJsonSafe={parseJsonSafe}
              extractActionProposalDetails={extractActionProposalDetails}
              extractFindingFieldUpdates={extractFindingFieldUpdates}
              extractActionFieldUpdates={extractActionFieldUpdates}
              actionFieldUpdateLabel={actionFieldUpdateLabel}
              findingFieldUpdateLabel={findingFieldUpdateLabel}
              describeWorkItemState={describeWorkItemState}
              describeThreadEvent={describeThreadEvent}
              canWithdrawActiveSyncPackage={canWithdrawActiveSyncPackage}
              onCloseThread={closeRecipientThread}
              onRecipientThreadMessageBodyChange={setRecipientThreadMessageBody}
              onPostRecipientThreadMessage={() => void postRecipientThreadMessage()}
              onRecipientWorkItemChange={updateRecipientSelectedWorkItem}
              onResolveRecipientSourceUpdateField={applyRecipientSourceUpdateFieldResolution}
              onWithdrawRecipientSyncPackage={(syncPackageId) => void withdrawRecipientSyncPackage(syncPackageId)}
              onAcknowledgeRecipientSourceUpdate={(syncPackageId) => void acknowledgeRecipientSourceUpdate(syncPackageId)}
              onWithdrawRecipientReturnedActionProposal={(proposalId) =>
                void withdrawRecipientReturnedActionProposal(proposalId)
              }
              onDownloadRecipientDeliveredEvidence={(artifactId, fileName) =>
                void downloadRecipientDeliveredEvidence(artifactId, fileName)
              }
              onRecipientSyncPackageFormChange={handleRecipientSyncPackageFormChange}
              onSaveRecipientSyncPackageDraft={() => void saveRecipientSyncPackageDraft()}
              onSubmitRecipientSyncPackage={() => void submitRecipientSyncPackage()}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Collaboration;

